import React, { useState, useEffect } from 'react';
import { 
  Menu, 
  LogOut, 
  Bell,
  User
} from 'lucide-react';
import { ChatList } from './ChatList';
import { ChatWindow } from './ChatWindow';
import { useChatStore } from '../../stores/chatStore';
import { useAuthStore } from '../../stores/authStore';
import { useAppStore } from '../../stores/appStore';
import { Chat } from '../../types';
import { BozLogo } from '../common/BozLogo';
import { UserProfile } from '../common/UserProfile';
import { MobileMenu } from '../common/MobileMenu';
import { DesktopNavMenu } from '../common/DesktopNavMenu';
import { PWAInstallPrompt } from '../common/PWAInstallPrompt';
import { SlotsPage } from '../admin/SlotsPage';
import { BookingsPage } from '../admin/BookingsPage';
import { InventoryPage } from '../admin/InventoryPage';
import AssignmentPage from '../admin/AssignmentPage';
import { AdminWorkPage } from '../admin/AdminWorkPage';
import { DestinationsPage } from '../admin/DestinationsPage';
import { ImageUploadPage } from '../admin/ImageUploadPage';

type PageType = 'chats' | 'slots' | 'bookings' | 'inventory' | 'assignment' | 'admin-work' | 'destinations' | 'image-upload';

const STORAGE_KEY_CURRENT_PAGE = 'boz_current_page';

// Load saved page from localStorage
const loadSavedPage = (): PageType => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY_CURRENT_PAGE);
    if (saved && ['chats', 'slots', 'bookings', 'inventory', 'assignment', 'admin-work', 'destinations', 'image-upload'].includes(saved)) {
      return saved as PageType;
    }
  } catch (error) {
    console.error('Failed to load saved page:', error);
  }
  return 'inventory'; // Default fallback
};

// Save page to localStorage
const savePage = (page: PageType) => {
  try {
    localStorage.setItem(STORAGE_KEY_CURRENT_PAGE, page);
  } catch (error) {
    console.error('Failed to save page:', error);
  }
};

export const ChatPage: React.FC = () => {
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [currentPage, setCurrentPage] = useState<PageType>(loadSavedPage);
  const [isMobile, setIsMobile] = useState(() => {
    // Initialize mobile state based on window size if available
    if (typeof window !== 'undefined') {
      return window.innerWidth < 1024;
    }
    return false;
  });
  const [showChatList, setShowChatList] = useState(true);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showDesktopNavMenu, setShowDesktopNavMenu] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [navigationHistory, setNavigationHistory] = useState<string[]>(() => [loadSavedPage()]);
  
  const { 
    activeChat, 
    setActiveChat, 
    loadChats, 
    loadAllChatsWithMessages,
    startPolling, 
    stopPolling, 
    stopMessagePolling, 
    loadMessages, 
    chats
  } = useChatStore();
  const { user, logout } = useAuthStore();
  const { theme, setTheme, toggleSidebar, sidebarOpen: appSidebarOpen } = useAppStore();

  // Sync sidebar state
  useEffect(() => {
    setSidebarOpen(appSidebarOpen);
  }, [appSidebarOpen]);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024; // lg breakpoint
      setIsMobile(mobile);
      
      // On mobile, if we're showing a chat, hide the chat list
      if (mobile && selectedChat) {
        setShowChatList(false);
      } else if (mobile && !selectedChat) {
        setShowChatList(true);
      }
      
      // Mark as initialized after first check
      if (!isInitialized) {
        setIsInitialized(true);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, [selectedChat, isInitialized]);

  // Load chats on mount and start polling only when Chats tab is open
  useEffect(() => {
    // Only load chats and start polling if Chats tab is currently active
    if (currentPage === 'chats') {
      // Use bulk loading on first load to fetch all chats and messages
      loadAllChatsWithMessages();
      startPolling();
    } else {
      // Stop polling when switching away from chats tab
      stopPolling();
      stopMessagePolling();
    }
    
    // Cleanup: stop polling when component unmounts or when switching tabs
    return () => {
      stopPolling();
      stopMessagePolling();
    };
  }, [currentPage, loadAllChatsWithMessages, startPolling, stopPolling, stopMessagePolling]);

  // Handle visibility change to pause/resume polling
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && currentPage === 'chats') {
        // Resume polling when tab becomes visible and we're on chats page
        startPolling();
      }
      // Note: Polling will automatically pause when tab is hidden (handled in store)
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentPage, startPolling]);

  // Handle Android back button
  useEffect(() => {
    const handleBackButton = (event: PopStateEvent) => {
      event.preventDefault();
      
      if (handleAndroidBack()) {
        // Prevent the default back behavior
        window.history.pushState(null, '', window.location.href);
      }
    };

    // Add a dummy state to prevent immediate back navigation
    window.history.pushState(null, '', window.location.href);
    
    window.addEventListener('popstate', handleBackButton);
    
    return () => {
      window.removeEventListener('popstate', handleBackButton);
    };
  }, [navigationHistory, selectedChat, isMobile, showMobileMenu, showDesktopNavMenu]);

  const handleChatSelect = async (chat: Chat) => {
    setSelectedChat(chat);
    setActiveChat(chat);
    // Load messages for the selected chat
    await loadMessages(chat.userId);
    
    // On mobile, hide the chat list and show the chat
    if (isMobile) {
      setShowChatList(false);
      // Add chat selection to navigation history
      setNavigationHistory(prev => [...prev, `chat-${chat.userId}`]);
    }
  };

  const handleBackToChatList = () => {
    setSelectedChat(null);
    setActiveChat(null);
    
    // On mobile, show the chat list
    if (isMobile) {
      setShowChatList(true);
    }
  };

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout?')) {
      await logout();
    }
  };

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  // Handle page navigation with history tracking
  const handlePageChange = (page: PageType) => {
    setCurrentPage(page);
    savePage(page); // Save to localStorage
    setNavigationHistory(prev => {
      const newHistory = [...prev];
      if (newHistory[newHistory.length - 1] !== page) {
        newHistory.push(page);
      }
      return newHistory;
    });
  };

  // Handle Android back button
  const handleAndroidBack = () => {
    if (navigationHistory.length > 1) {
      // Go back in navigation history
      const newHistory = [...navigationHistory];
      const currentState = newHistory.pop(); // Remove current page
      
      // Check if we're currently viewing a chat
      if (currentState && currentState.startsWith('chat-')) {
        // Go back to chat list
        handleBackToChatList();
        setNavigationHistory(newHistory);
        return true;
      } else {
        // Go back to previous page
        const previousPage = newHistory[newHistory.length - 1] as 'chats' | 'slots' | 'bookings' | 'inventory' | 'assignment' | 'admin-work' | 'destinations' | 'image-upload';
        setNavigationHistory(newHistory);
        setCurrentPage(previousPage);
        
        // Close any open menus
        setShowMobileMenu(false);
        setShowDesktopNavMenu(false);
        
        return true; // Prevent default back behavior
      }
    } else if (selectedChat && isMobile) {
      // If on mobile and viewing a chat, go back to chat list
      handleBackToChatList();
      return true; // Prevent default back behavior
    } else if (showMobileMenu || showDesktopNavMenu) {
      // If menu is open, close it
      setShowMobileMenu(false);
      setShowDesktopNavMenu(false);
      return true; // Prevent default back behavior
    }
    
    return false; // Allow default back behavior (close app)
  };

  // Calculate total unread count
  const totalUnreadCount = chats.reduce((total, chat) => total + chat.unreadCount, 0);

  // Show loading screen until initialized to prevent flicker
  if (!isInitialized) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center mx-auto mb-4">
            <BozLogo size={32} />
          </div>
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-screen flex flex-col bg-gray-50 ${isMobile ? 'mobile-view' : 'desktop-view'}`}>
      {/* Top Navigation */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          {/* Mobile Menu Button */}
          {isMobile ? (
            <button
              onClick={() => setShowMobileMenu(true)}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          ) : (
            <>
              {/* Desktop Navigation Menu Button */}
              <button
                onClick={() => setShowDesktopNavMenu(true)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Open navigation menu"
              >
                <Menu className="h-5 w-5" />
              </button>
              {/* Desktop Sidebar Toggle Button */}
              <button
                onClick={() => toggleSidebar()}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors lg:hidden"
                aria-label="Toggle sidebar"
              >
                <Menu className="h-5 w-5" />
              </button>
            </>
          )}
          
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center">
              <BozLogo size={32} />
            </div>
            <div className="flex flex-col">
              <h1 className="text-lg font-semibold text-gray-900">Boz</h1>
              {(isMobile || !isMobile) && (
                <span className="text-xs text-gray-500 capitalize">
                  {currentPage === 'slots' ? 'Trip Slots' : currentPage}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Navigation Tabs - Hidden */}
        {/* <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setCurrentPage('chats')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              currentPage === 'chats'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Chats
          </button>
          <button
            onClick={() => setCurrentPage('slots')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              currentPage === 'slots'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Trip Slots
          </button>
          <button
            onClick={() => setCurrentPage('bookings')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              currentPage === 'bookings'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Bookings
          </button>
          <button
            onClick={() => setCurrentPage('inventory')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              currentPage === 'inventory'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Inventory
          </button>
        </div> */}

        <div className="flex items-center space-x-2">
          {/* Notifications */}
          <button
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors relative"
            aria-label="Notifications"
          >
            <Bell className="h-5 w-5" />
            {totalUnreadCount > 0 && (
              <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">
                {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
              </span>
            )}
          </button>

          {/* Audio Settings - Hidden */}
          {/* <AudioSettings /> */}

          {/* Theme Toggle - Hidden */}
          {/* <button
            onClick={toggleTheme}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}
          >
            {theme === 'light' ? (
              <Moon className="h-5 w-5" />
            ) : (
              <Sun className="h-5 w-5" />
            )}
          </button> */}

          {/* Settings - Hidden */}
          {/* <button
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Settings"
          >
            <Settings className="h-5 w-5" />
          </button> */}

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => setShowUserProfile(true)}
              className="flex items-center space-x-2 p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="User menu"
            >
              {user?.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.name}
                  className="h-8 w-8 rounded-full object-cover"
                />
              ) : (
                <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <User className="h-4 w-4 text-primary-600" />
                </div>
              )}
              <span className="hidden md:block text-sm font-medium">
                {user?.name}
              </span>
            </button>
          </div>

          {/* Logout */}
          <button
            onClick={handleLogout}
            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            aria-label="Logout"
          >
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {currentPage === 'chats' ? (
          <>
            {/* Desktop Layout */}
            {!isMobile ? (
              <>
                {/* Sidebar */}
                <div className={`${
                  sidebarOpen ? 'w-80' : 'w-0'
                } transition-all duration-300 ease-in-out flex-shrink-0 lg:w-80 lg:block`}>
                  {sidebarOpen && (
                    <div className="h-full">
                    <ChatList
                      onChatSelect={handleChatSelect}
                      activeChatId={selectedChat?.userId}
                    />
                    </div>
                  )}
                </div>

                {/* Chat Area */}
                <div className="flex-1 flex flex-col min-w-0">
                  {selectedChat ? (
                    <ChatWindow
                      chat={selectedChat}
                      onBack={handleBackToChatList}
                    />
                  ) : (
                    <div className="flex-1 flex items-center justify-center bg-gray-50">
                      <div className="text-center">
                        <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                          <User className="h-12 w-12 text-gray-400" />
                        </div>
                        <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                          Welcome to Boz Sales
                        </h2>
                        <p className="text-gray-600 max-w-md">
                          Select a conversation from the sidebar to start chatting with your sales team.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              /* Mobile Layout */
              <>
                {/* Chat List Page */}
                {showChatList && (
                  <div className="flex-1 flex flex-col min-w-0">
                    <ChatList
                      onChatSelect={handleChatSelect}
                      activeChatId={selectedChat?.userId}
                    />
                  </div>
                )}

                {/* Individual Chat Page */}
                {!showChatList && selectedChat && (
                  <div className="flex-1 flex flex-col min-w-0">
                    <ChatWindow
                      chat={selectedChat}
                      onBack={handleBackToChatList}
                    />
                  </div>
                )}
              </>
            )}
          </>
        ) : currentPage === 'slots' ? (
          <div className="flex-1">
            <SlotsPage />
          </div>
        ) : currentPage === 'bookings' ? (
          <div className="flex-1">
            <BookingsPage />
          </div>
        ) : currentPage === 'assignment' ? (
          <div className="flex-1">
            <AssignmentPage />
          </div>
        ) : currentPage === 'admin-work' ? (
          <div className="flex-1">
            <AdminWorkPage />
          </div>
        ) : currentPage === 'destinations' ? (
          <div className="flex-1 overflow-y-auto min-h-0">
            <DestinationsPage />
          </div>
        ) : currentPage === 'image-upload' ? (
          <div className="flex-1 overflow-y-auto min-h-0">
            <ImageUploadPage />
          </div>
        ) : (
          <div className="flex-1">
            <InventoryPage />
          </div>
        )}
      </div>

      {/* User Profile Modal */}
      <UserProfile
        isOpen={showUserProfile}
        onClose={() => setShowUserProfile(false)}
      />

      {/* Mobile Menu */}
      <MobileMenu
        isOpen={showMobileMenu}
        onClose={() => setShowMobileMenu(false)}
        currentPage={currentPage}
        onPageChange={handlePageChange}
      />

      {/* Desktop Navigation Menu */}
      <DesktopNavMenu
        isOpen={showDesktopNavMenu}
        onClose={() => setShowDesktopNavMenu(false)}
        currentPage={currentPage}
        onPageChange={handlePageChange}
      />

      {/* PWA Install Prompt */}
      <PWAInstallPrompt />
    </div>
  );
};
